import { OpenTemplateEditorBlock } from '@/components/ui/work-tool-cards/OpenTemplateEditorBlock';
import { TemplatePinBlock } from '@/components/ui/work-tool-cards/TemplatePinBlock';
import type { ToolUseItem } from '@heidi/agents/contracts';
import { friendlyToolError, setOpenArtifactAtom } from '@heidi/agents/data';
import { useSetAtom } from 'jotai';
import type { ComponentProps } from 'react';

import { readToolResultRecord } from '@heidi/agents/contracts';
import { readString, readToolData, readToolError } from './shared/readers';
import { toCreateTemplateStatus } from './status';

/**
 * Supplies the artefact-open effect the pin card no longer reaches for itself.
 * Projects owns where a template opens; the card only renders the affordance.
 */
// eslint-disable-next-line react-refresh/only-export-components -- this module's exports are tool renderers, not components, and the wrapper cannot become its own module: the projects UI intake ratchet reserves new modules here for @heidi/agents
const PinTemplateCard = (
  props: Omit<ComponentProps<typeof TemplatePinBlock>, 'onOpenTemplate'>
): JSX.Element => {
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);

  return (
    <TemplatePinBlock
      {...props}
      onOpenTemplate={(templateId) =>
        setOpenArtifact({ type: 'template-builder', templateId })
      }
    />
  );
};

/** The same wiring for the open-template card, whose click opens the same panel. */
// eslint-disable-next-line react-refresh/only-export-components -- this module's exports are tool renderers, not components, and the wrapper cannot become its own module: the projects UI intake ratchet reserves new modules here for @heidi/agents
const OpenTemplateEditorCard = (
  props: Omit<ComponentProps<typeof OpenTemplateEditorBlock>, 'onOpenTemplate'>
): JSX.Element => {
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);

  return (
    <OpenTemplateEditorBlock
      {...props}
      onOpenTemplate={(templateId) =>
        setOpenArtifact({ type: 'template-builder', templateId })
      }
    />
  );
};

const readTemplatePayload = (item: ToolUseItem) => {
  const data = readToolData(item) ?? readToolResultRecord(item);
  return {
    templateId: readString(data, 'template_id') ?? readString(item.input, 'template_id'),
    templateName:
      readString(data, 'template_name') ?? readString(item.input, 'template_name'),
  };
};

export const renderOpenTemplateEditorToolUse = (
  item: ToolUseItem
): JSX.Element | null => {
  const status = toCreateTemplateStatus(item.status);
  if (!status) return null;

  const { templateId, templateName } = readTemplatePayload(item);

  // Without anything to identify the template the rich block carries no more
  // information than GenericToolUseRow — fall through cleanly.
  if (!templateId && !templateName && status !== 'error') return null;

  if (status === 'error') {
    return (
      <OpenTemplateEditorCard
        status="error"
        templateId={templateId}
        templateName={templateName}
        error={friendlyToolError(readToolError(item))}
      />
    );
  }

  return (
    <OpenTemplateEditorCard
      status={status}
      templateId={templateId}
      templateName={templateName}
    />
  );
};

export const renderPinTemplateToolUse = (item: ToolUseItem): JSX.Element | null => {
  const status = toCreateTemplateStatus(item.status);
  if (!status) return null;

  const { templateId, templateName } = readTemplatePayload(item);
  if (!templateId && !templateName && status !== 'error') return null;

  if (status === 'error') {
    return (
      <PinTemplateCard
        variant="pin"
        status="error"
        templateId={templateId}
        templateName={templateName}
        error={friendlyToolError(readToolError(item))}
      />
    );
  }

  return (
    <PinTemplateCard
      variant="pin"
      status={status}
      templateId={templateId}
      templateName={templateName}
    />
  );
};

export const renderUnpinTemplateToolUse = (item: ToolUseItem): JSX.Element | null => {
  const status = toCreateTemplateStatus(item.status);
  if (!status) return null;

  const { templateId, templateName } = readTemplatePayload(item);
  if (!templateId && !templateName && status !== 'error') return null;

  if (status === 'error') {
    return (
      <PinTemplateCard
        variant="unpin"
        status="error"
        templateId={templateId}
        templateName={templateName}
        error={friendlyToolError(readToolError(item))}
      />
    );
  }

  return (
    <PinTemplateCard
      variant="unpin"
      status={status}
      templateId={templateId}
      templateName={templateName}
    />
  );
};
